import { BrowserLogger, Metadata } from "@vp/front-end-logger";
import { v4 as uuidv4 } from "uuid";
import { Logger as PipelineLogger } from "@vp/winston-logger";
import "dotenv/config";
import { transports } from "winston";
import { URL } from "url";
import PageConfig from "../utils/PageConfig";

const NEW_RELIC_URL = "https://log-api.newrelic.com";
const SERVICE = "productpagesv2";
const SERVICE_PIPELINE = `${SERVICE}-pipeline`;

interface ILogger {
  debug: (message: string, context?: Metadata) => void;
  info: (message: string, context?: Metadata) => void;
  warning: (message: string, context?: Metadata) => void;
  error: (message: string, context?: Metadata) => void;
}
export class Logger {
  private static _browserInstance: ILogger;

  private constructor() {}
  public static get instance(): ILogger {
    if (PageConfig.isSSR) {
      return PipelineLogger.instance;
    }
    if (Logger._browserInstance === undefined) {
      Logger._browserInstance = PageConfig.isProduction
        ? new BrowserLoggerWrapper(SERVICE)
        : new DevLogger();
    }

    return Logger._browserInstance;
  }
}

class DevLogger implements ILogger {
  info(message: string, context?: Metadata): void {
    console.info(message, context);
  }
  error(message: string, context?: Metadata): void {
    console.error(message, context);
  }
  debug(message: string, context?: Metadata): void {
    console.debug(message, context);
  }
  warning(message: string, context?: Metadata): void {
    console.warn(message, context);
  }
}

class BrowserLoggerWrapper implements ILogger {
  private _browserInstance: BrowserLogger;
  constructor(service: string) {
    this._browserInstance = new BrowserLogger(service);
  }
  info(message: string, context?: Metadata): void {
    this._browserInstance.info(message, context).catch(_e => console.log("Error logging"));
  }
  error(message: string, context?: Metadata): void {
    this._browserInstance.error(message, context).catch(_e => console.log("Error logging"));
  }
  debug(message: string, context?: Metadata): void {
    this._browserInstance.debug(message, context).catch(_e => console.log("Error logging"));
  }
  warning(message: string, context?: Metadata): void {
    this._browserInstance.warning(message, context).catch(_e => console.log("Error logging"));
  }
}

export function initializePipelineLogger(context: Record<string, unknown> | undefined): void {
  const licenseKey = process.env.NEW_RELIC_INGEST_LICENSE_KEY;
  const logTargetUrl = new URL(process.env.LOG_TARGET_URL ?? NEW_RELIC_URL);
  const executionId = uuidv4();
  const builder = PipelineLogger.getBuilder()
    .withConsoleTransport()
    .withContext({
      ...context,
      executionId: executionId,
      service: SERVICE_PIPELINE,
    });
  if (licenseKey && PageConfig.isProduction) {
    const transport = new transports.Http({
      host: logTargetUrl.hostname,
      path: "/log/v1",
      headers: {
        "Api-Key": licenseKey,
      },
      ssl: logTargetUrl.protocol === "https:",
      port: logTargetUrl.port ? parseInt(logTargetUrl.port) : undefined,
      batch: true,
    });
    builder.withTransport(transport);
  }

  builder.build();
}
